// Mic button for the chat composer. Push-to-toggle: click to record,
// click again to stop and transcribe. Result is delivered via
// onTranscript(text). Admin-only via feature flag; the caller controls
// whether to render this component.

import { useEffect, useRef, useState } from "react";
import { Mic, Loader2, Square } from "lucide-react";
import { toast } from "sonner";
import { startRecording, transcribeAudio, isVoiceSupported } from "@/lib/voice";

// Visual states: idle (mic), recording (pulsing dot + stop), transcribing (spinner).
export default function VoiceInputButton({ onTranscript, disabled, className = "" }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      // Safety: cancel any in-flight recording on unmount so the mic
      // indicator doesn't stay lit.
      try {
        recRef.current?.cancel?.();
      } catch {
        /* ignore */
      }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startTimer = () => {
    startedAtRef.current = Date.now();
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 250);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const begin = async () => {
    if (!isVoiceSupported()) {
      toast.error("Voice recording isn't supported in this browser.");
      return;
    }
    try {
      recRef.current = await startRecording();
      setState("recording");
      startTimer();
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "Microphone permission denied." : "Couldn't access microphone.");
    }
  };

  const finish = async () => {
    stopTimer();
    if (!recRef.current) return;
    setState("transcribing");
    try {
      const blob = await recRef.current.stop();
      recRef.current = null;
      const { text } = await transcribeAudio(blob);
      if (text && text.trim()) {
        onTranscript?.(text.trim());
      } else {
        toast.error("Nothing to transcribe — try again.");
      }
    } catch (e) {
      if (e?.empty) {
        toast("Didn't catch that — try speaking a little louder.");
      } else {
        toast.error(e?.message || "Transcription failed.");
      }
    } finally {
      setState("idle");
    }
  };

  const cancel = () => {
    stopTimer();
    try {
      recRef.current?.cancel?.();
    } catch {
      /* ignore */
    }
    recRef.current = null;
    setState("idle");
  };

  const onClick = () => {
    if (state === "idle") return begin();
    if (state === "recording") return finish();
  };

  const secondsLabel = `${String(Math.floor(elapsed / 60)).padStart(1, "0")}:${String(elapsed % 60).padStart(2, "0")}`;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {state === "recording" && (
        <>
          <span
            className="text-[11px] font-mono-ui text-[var(--accent)] tabular-nums"
            data-testid="voice-elapsed"
          >
            {secondsLabel}
          </span>
          <button
            type="button"
            onClick={cancel}
            className="text-[10px] font-mono-ui uppercase tracking-[0.2em] text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
            data-testid="voice-cancel"
            title="Cancel"
          >
            Cancel
          </button>
        </>
      )}
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || state === "transcribing"}
        aria-label={state === "recording" ? "Stop and transcribe" : "Record voice input"}
        title={state === "recording" ? "Stop and transcribe" : "Voice input"}
        data-testid="voice-mic"
        className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
          state === "recording"
            ? "bg-[var(--accent)] text-white animate-pulse"
            : "text-[var(--accent)]/70 hover:text-[var(--accent)] hover:bg-[var(--accent)]/10"
        } disabled:opacity-30 disabled:cursor-not-allowed`}
      >
        {state === "transcribing" ? (
          <Loader2 size={17} className="animate-spin" />
        ) : state === "recording" ? (
          <Square size={13} strokeWidth={2.5} fill="currentColor" />
        ) : (
          <Mic size={17} />
        )}
      </button>
    </div>
  );
}
